<template>
  <div class="page-container">
    <div class="title-header">
      <div class="title">Kampanya Detayı</div>
    </div>

    <div v-if="!isLoading && !campaignData.name" class="empty-state">
      <div class="empty-state-content">
        <div class="empty-state-icon">
          <img
            class="empty-state-icon-img"
            src="@/assets/images/empty-search.png"
            alt="Ürün bulunamadı"
          />
        </div>
        <div class="empty-state-title">Aradığınız kampanya bulunamadı.</div>
        <div class="empty-state-description">
          Katılabileceğiniz ve katıldığınız kampanyaları Sepette İndirim
          Kampanya Teklifleri bölümünde inceleyebilirsiniz.
        </div>
        <AppButton
          secondary
          md
          title="Kampanya Tekliflerine Git"
          @buttonClick="goToCampaignOffers"
        />
      </div>
    </div>
    <div v-else-if="campaignData.name" class="campaign-detail-wrapper">
      <div class="campaign-detail">
        <div class="campaign-detail-header">
          <div class="campaign-detail-header-title">
            <Icons name="Announcements" color="#1C1C1E" size="md" />
            <span>Kampanya Bilgisi</span>
          </div>
        </div>

        <div class="campaign-info-content">
          <div class="campaign-info-top">
            <div class="info-item">
              <div class="info-label">Kampanya Adı</div>
              <div class="info-value">{{ campaignData.name }}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Kampanyaya Uygun Ürün Sayısı</div>
              <div class="info-value">
                {{ campaignData.eligibleProductCount }}
              </div>
            </div>
            <!--<div class="info-item">
              Şimdilik devre dışı: Son 24 Saatteki Satış Sayısı
              <div class="info-label">Son 24 Saatteki Satış Sayısı</div>
              <div class="info-value">{{ campaignData.last24HoursSales }}</div>
             
              <div class="info-label">&nbsp;</div>
              <div class="info-value">&nbsp;</div>
            </div>-->
             <div class="info-item" v-if="campaignData.commissionDiscount">
              <div class="info-label">Komisyon Bilgisi</div>
              <div class="info-value">
                %{{ campaignData.commissionDiscount }} Komisyon İndirimi
              </div>
            </div>
          </div>

          <div class="campaign-info-bottom">
            <div class="info-item">
              <div class="info-label">Kampanya Tarihleri</div>
              <div class="info-value">{{ campaignData.dateRange }}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Kampanyaya Katılan Satıcı Sayısı</div>
              <div class="info-value">
                {{ campaignData.participatingSellerCount }}
              </div>
            </div>
          </div>
        </div>

        <div class="campaign-conditions">
          <div class="conditions-title">Kampanya koşulları nelerdir?</div>
          <ul class="conditions-list">
            <li>
              İndirim tutarı, %{{ campaignData.n11Share }} oranında n11, %{{
                campaignData.sellerShare
              }}
              oranında satıcı tarafından karşılanmaktadır.
            </li>
            <li v-if="campaignData.canBeUsedWithVoucher">
              Kampanya kuponlarla birleştirilebilir.
            </li>
            <li v-if="campaignData.quantityBasedCascading">
              Bu kampanyada sepette katlanan indirim uygulanır. Belirlenen
              indirim tutarı, minimum sepet tutarına ulaşıldığında otomatik
              olarak düşülür. Sepete birden fazla ürün eklenirse indirim, her
              ürün için ayrı ayrı uygulanır.
            </li>
          </ul>
        </div>
      </div>
      <div
        v-if="campaignData.campaignHtml && !isManageMode"
        class="campaign-why-join"
      >
        <div class="campaign-why-join-title">
          <span v-html="campaignData.campaignHtml"></span>
        </div>
      </div>
      <div v-if="isManageMode" class="campaign-performance">
        <div class="campaign-performance-title">
          <Icons name="CampaignInfo" color="#1C1C1E" size="lg" />
          <span>Kampanya Performansı</span>
        </div>
        <div class="campaign-performance-items">
          <div class="campaign-performance-item">
            <span>Satış Adedi</span>
            <span>{{ campaignData.sellerOrderCount }}</span>
          </div>
          <div class="campaign-performance-item">
            <span>Toplam GMV</span>
            <span>{{ campaignData.sellerTotalGMV }}</span>
          </div>
        </div>
      </div>
      <div v-if="!isManageMode" class="sticky-footer">
        <AppButton
          primary
          md
          title="Kampanyaya Katıl"
          @buttonClick="joinCampaign"
        />
      </div>
      <div v-else class="sticky-footer">
        <AppButton
          primary
          md
          title="Kampanyayı Yönet"
          @buttonClick="joinCampaign"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Icons, AppButton } from '@n11/so-ui-components';
import { sendButtonClickEvent, sendPageViewEvent } from '@/utils/ga4/helper';
import { getCampaignDetail } from './services/umbrellaCampaignService';

const route = useRoute();
const router = useRouter();
const campaignId = ref(null);
const mode = ref(sessionStorage.getItem('campaignDetailMode') || 'detail'); // 'detail' veya 'manage'
const isLoading = ref(false);
const campaignData = ref({
  name: '',
  dateRange: '',
  eligibleProductCount: 0,
  participatingSellerCount: 0,
  last24HoursSales: 0,
  campaignHtml: '',
  sellerShare: 0,
  n11Share: 0,
  commissionDiscount: null,
  canBeUsedWithVoucher: false,
  quantityBasedCascading: false,
  orderCountLast24Hours: 0,
  sellerOrderCount: 0,
  sellerTotalGMV: 0,
});

const isManageMode = computed(() => mode.value === 'manage');

const sendCampaignDetailPageView = () => {
  sendPageViewEvent({
    page_name: 'Kampanya Detayı',
    page_type: 'Campaign Detail',
    campaign_id: campaignId.value,
    campaign_name: campaignData.value.name || '',
  });
};

const fetchCampaignDetail = async () => {
  if (!campaignId.value) return;

  isLoading.value = true;
  try {
    const data = await getCampaignDetail(campaignId.value);
    campaignData.value = {
      name: data.name || '',
      dateRange: data.dateRange || '',
      eligibleProductCount: data.eligibleProductCount || 0,
      participatingSellerCount: data.participatingSellerCount || 0,
      last24HoursSales: data.last24HoursSales || 0,
      campaignHtml: data.campaignHtml || '',
      sellerShare: data.sellerShare || 0,
      n11Share: data.n11Share || 0,
      commissionDiscount: data.commissionDiscount ?? null,
      canBeUsedWithVoucher: data.canBeUsedWithVoucher || false,
      quantityBasedCascading: data.quantityBasedCascading || false,
      orderCountLast24Hours: data.orderCountLast24Hours || 0,
      sellerOrderCount: data.sellerOrderCount ?? 0,
      sellerTotalGMV: data.sellerTotalGMV ?? 0,
    };
    sendCampaignDetailPageView();
  } catch (error) {
    console.error('Error fetching campaign detail:', error);
  } finally {
    isLoading.value = false;
  }
};

const joinCampaign = () => {
  if (!campaignId.value) return;
  sendButtonClickEvent({
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    button_name: 'Kampanyaya Katıl',
    button_type: 'Join Campaign',
    page_type: 'Campaign Detail',
  });

  if (mode.value === 'manage') {
    sendButtonClickEvent({
      page_path: typeof window !== 'undefined' ? window.location.pathname : '',
      button_name: 'Kampanyayı Yönet',
      button_type: 'Edit Campaign',
      page_type: 'Campaign Detail',
    });
    router.push({
      name: 'ManageCampaign',
      params: { campaignId: campaignId.value.toString() },
    });
  } else {
    router.push({
      name: 'JoinCampaign',
      params: { campaignId: campaignId.value.toString() },
    });
  }
};

const goToCampaignOffers = () => {
  router.push('/campaigns/umbrella');
};

onMounted(() => {
  campaignId.value =
    sessionStorage.getItem('campaignDetailId') ||
    route.query.campaignId ||
    route.params.campaignId;
  fetchCampaignDetail();
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';

.page-container {
  display: flex;
  width: 100%;
  flex-direction: column;

  .title-header {
    padding: 16px 0;

    .title {
      color: $colorN80;
      font-size: 20px;
      font-weight: 700;
      letter-spacing: -0.2px;
      line-height: 26px;
    }
  }

  .campaign-detail {
    border-radius: 8px;
    background: $colorN00;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .campaign-detail-header {
      &-title {
        display: flex;
        align-items: center;
        gap: 4px;
        color: $colorN80;
        font-size: 16px;
        font-weight: 700;
        line-height: 20px;
        letter-spacing: -0.05px;

        span {
          color: $colorN80;
          font-size: 18px;
          font-weight: 700;
          line-height: 22px;
          letter-spacing: -0.1px;
        }
      }
    }

    .campaign-info-content {
      display: flex;
      flex-direction: column;
      gap: 16px;
      width: 100%;

      .campaign-info-top {
        display: flex;
        gap: 48px;
        width: 100%;

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 0 1 calc((100% - 96px) / 3);

          .info-label {
            color: $colorN80;
            font-size: 14px;
            font-weight: 400;
            line-height: 18px;
            letter-spacing: -0.05px;
          }

          .info-value {
            overflow: hidden;
            color: $colorN80;
            font-size: 16px;
            font-weight: 700;
            line-height: 20px;
            letter-spacing: -0.05px;
          }
        }
      }

      .campaign-info-bottom {
        display: flex;
        gap: 48px;
        width: 100%;
        justify-content: flex-start;

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 0 1 calc((100% - 96px) / 3);

          .info-label {
            color: #1c1c1e;
            font-size: 14px;
            font-weight: 400;
            line-height: 18px;
            letter-spacing: -0.05px;
          }

          .info-value {
            overflow: hidden;
            color: #1c1c1e;
            font-size: 16px;
            font-weight: 700;
            line-height: 20px;
            letter-spacing: -0.05px;
          }
        }
      }
    }

    .campaign-conditions {
      border-radius: 8px;
      background: $colorB05;
      padding: 24px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      border: 1px solid $colorB50;

      .conditions-title {
        color: $colorN80;
        font-size: 14px;
        font-weight: 700;
        line-height: 20px;
        letter-spacing: -0.05px;
      }

      .conditions-list {
        margin: 0;
        padding-left: 20px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        list-style: disc;

        li {
          color: $colorN80;
          font-size: 13px;
          font-weight: 400;
          line-height: 18px;
          letter-spacing: -0.05px;
        }
      }
    }
  }

  .campaign-why-join {
    padding: 24px;
    margin: 16px 0;
    border-radius: 8px;
    background: $colorN00;
    border: 1px solid $colorB50;

    span {
      color: $colorN80;
      font-size: 16px;
      line-height: 20px;
      letter-spacing: -0.05px;
    }
  }

  .campaign-performance {
    padding: 24px;
    margin: 16px 0;
    border-radius: 8px;
    background: $colorN00;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .campaign-performance-title {
      display: flex;
      gap: 4px;
      align-items: center;
      justify-content: flex-start;
      color: $colorN80;
      font-size: 16px;
      font-weight: 700;
      line-height: 20px;
      letter-spacing: -0.05px;
    }

    .campaign-performance-items {
      display: flex;
      flex-direction: row;
      gap: 48px;
      width: 100%;

      .campaign-performance-item {
        display: flex;
        flex-direction: column;
        gap: 6px;
        flex: 1;

        span:first-child {
          color: $colorN80;
          font-size: 14px;
          font-weight: 400;
          line-height: 18px;
          letter-spacing: -0.05px;
        }

        span:last-child {
          color: $colorN80;
          font-size: 16px;
          font-weight: 700;
          line-height: 20px;
          letter-spacing: -0.05px;
        }
      }
    }
  }

  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 200px;
    .empty-state-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;

      .empty-state-icon {
        .empty-state-icon-img {
          width: 180px;
          height: 180px;
        }
      }

      .empty-state-title {
        color: $colorN80;
        text-align: center;
        font-size: 20px;
        font-weight: 600;
        line-height: 26px;
        letter-spacing: -0.2px;
        display: block;
      }

      .empty-state-description {
        color: $colorN80;
        text-align: center;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: -0.05px;
        display: block;
      }

      :deep(.btn) {
        width: 255px !important;
      }
    }
  }
}

.campaign-detail-wrapper {
  padding-bottom: 100px;
}

.sticky-footer {
  position: fixed;
  z-index: 10;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  padding: 20px 200px;
  border-top: 0.5px solid $colorN30;
  background: $colorN00;

  :deep(button) {
    width: 193px;
  }
}
</style>
